
import { UserProfile } from "@/types";
import { Hourglass, Scale, TrendingDown, TrendingUp, Activity, User } from "lucide-react";
import clsx from "clsx";

interface LongevityCardProps {
    chronologicalAge: number;
    longevityAge: number | null;
    longevityScore: number | null;
    weight: number;
    height: number;
    className?: string;
}

export default function LongevityCard({ chronologicalAge, longevityAge, longevityScore, weight, height, className }: LongevityCardProps) {
    const ageDiff = longevityAge !== null ? longevityAge - chronologicalAge : 0;
    const isGood = ageDiff <= 0;
    const hasData = longevityAge !== null && longevityScore !== null;

    // Calculate BMI for display
    const heightM = height / 100;
    const bmi = height > 0 ? (weight / (heightM * heightM)).toFixed(1) : "0.0";

    return (
        <div className={clsx("relative group rounded-2xl", className)}>
            {/* Animated Green Border */}
            <div className="absolute -inset-[1px] rounded-2xl overflow-hidden">
                <div className="absolute inset-[-100%] animate-border-spin bg-[conic-gradient(from_90deg_at_50%_50%,#00000000_50%,#22c55e_100%)] opacity-70" />
            </div>

            {/* Inner Content Card */}
            <div className="glass-card relative h-full w-full bg-[#020617]/90 backdrop-blur-xl p-4 rounded-2xl border border-white/5">
                <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                        <Hourglass size={16} className="text-primary" />
                        <h2 className="text-xs font-bold uppercase tracking-widest text-text-muted">Longevity Age</h2>
                    </div>
                    {/* Badge based on diff */}
                    {hasData ? (
                        <div className={clsx(
                            "px-2 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1",
                            isGood ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" : "bg-rose-500/10 border-rose-500/20 text-rose-400"
                        )}>
                            {isGood ? <TrendingDown size={10} /> : <TrendingUp size={10} />}
                            {Math.abs(ageDiff).toFixed(1)} yrs {isGood ? "younger" : "older"}
                        </div>
                    ) : (
                        <div className="px-2 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1 bg-white/5 border-white/10 text-text-muted">
                            Fill data
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between mt-2">
                    {/* Main Metric */}
                    <div>
                        <div className="text-3xl font-bold text-white flex items-baseline gap-2">
                            {hasData && longevityAge !== null ? longevityAge.toFixed(1) : "--"}
                            <span className="text-xs font-normal text-text-muted">los age</span>
                        </div>
                    </div>

                    {/* Secondary Metrics */}
                    <div className="flex gap-4">
                        <div className="flex flex-col items-end">
                            <span className="text-[10px] text-text-muted uppercase tracking-wider font-bold">Score</span>
                            <span className={clsx(
                                "text-lg font-semibold",
                                !hasData ? "text-gray-500" :
                                    longevityScore >= 90 ? "text-emerald-400" :
                                        longevityScore >= 75 ? "text-emerald-400/80" :
                                            longevityScore >= 50 ? "text-yellow-400" : "text-rose-400"
                            )}>{hasData ? longevityScore : "-"}</span>
                        </div>

                        <div className="h-8 w-px bg-white/10"></div>

                        <div className="flex flex-col items-end">
                            <span className="text-[10px] text-text-muted uppercase tracking-wider font-bold">Real Age</span>
                            <span className="text-lg font-semibold text-gray-300">{chronologicalAge}</span>
                        </div>

                        <div className="h-8 w-px bg-white/10"></div>

                        <div className="flex flex-col items-end">
                            <span className="text-[10px] text-text-muted uppercase tracking-wider font-bold">Weight</span>
                            <span className="text-lg font-semibold text-gray-300">{weight} <span className="text-[10px] text-text-muted font-normal">kg</span></span>
                        </div>
                    </div>
                </div>

                {/* Progress Bar Visual */}
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs text-text-muted">
                    <div className="flex items-center gap-1">
                        <Activity size={12} className="text-gray-500" />
                        <span>BMI: <span className="text-gray-300 font-medium">{bmi}</span></span>
                    </div>
                    <div>
                        {hasData ? (
                            isGood ? (
                                <span className="text-emerald-400">Excellent pace</span>
                            ) : (
                                <span className="text-rose-400">Aging accelerated</span>
                            )
                        ) : (
                            <span className="text-gray-500">Log data to see impact</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
